import React, { useState, useEffect } from 'react'
import { useAuth } from '../AuthContext'
import { supabase } from '../supabaseClient'
import { Input, Button } from './UI'
import { useTheme } from '../ThemeContext'
import {
  checkLockout,
  recordFailedAttempt,
  clearLockout,
  getLockoutMessage,
  formatLockoutTime
} from '../utils/loginLockout'
import { logAuditEvent, AUDIT_ACTIONS } from '../utils/auditLog'

export default function Login() {
  const { spacing } = useTheme()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [showForgotPassword, setShowForgotPassword] = useState(false)
  const [resetEmail, setResetEmail] = useState('')
  const [resetMessage, setResetMessage] = useState('')
  const [resetLoading, setResetLoading] = useState(false)
  const [showPasswordReset, setShowPasswordReset] = useState(false)
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [passwordResetMessage, setPasswordResetMessage] = useState('')
  const [lockoutStatus, setLockoutStatus] = useState(null)
  const [countdown, setCountdown] = useState(0)
  const { signIn } = useAuth()

  // Check lockout status on mount and start countdown timer
  useEffect(() => {
    const status = checkLockout()
    if (status && status.isLocked) {
      setLockoutStatus(status)
      setCountdown(status.remainingSeconds)
    }
  }, [])

  // Countdown timer for lockout
  useEffect(() => {
    if (!lockoutStatus || !lockoutStatus.isLocked) return

    const timer = setInterval(() => {
      const status = checkLockout()
      if (!status || !status.isLocked) {
        // Lockout expired
        setLockoutStatus(null)
        setCountdown(0)
        setError('')
      } else {
        setCountdown(status.remainingSeconds)
      }
    }, 1000)

    return () => clearInterval(timer)
  }, [lockoutStatus])

  // Check if user is coming from password reset email
  useEffect(() => {
    const hashParams = new URLSearchParams(window.location.hash.substring(1))
    const type = hashParams.get('type')
    const accessToken = hashParams.get('access_token')

    if (type === 'recovery' && accessToken) {
      setShowPasswordReset(true)
    }
  }, [])

  const handleSubmit = async (e) => {
    e.preventDefault()

    // Check if account is locked out
    const lockout = checkLockout()
    if (lockout && lockout.isLocked) {
      setError(getLockoutMessage(lockout))
      setLockoutStatus(lockout)
      setCountdown(lockout.remainingSeconds)
      return
    }

    try {
      setError('')
      setLoading(true)

      // Small delay to allow browser extensions (like LastPass) to capture credentials
      await new Promise(resolve => setTimeout(resolve, 100))

      await signIn(email, password)

      // Clear lockout on successful login
      clearLockout()
      await logAuditEvent(AUDIT_ACTIONS.LOGIN, { email })
    } catch (error) {
      setLoading(false)

      // Record failed attempt
      const failedStatus = recordFailedAttempt()

      // Log failed login attempt (direct insert — no auth context available)
      supabase.from('audit_logs').insert([{
        action: AUDIT_ACTIONS.LOGIN_FAILED,
        user_email: email,
        details: { reason: error.message }
      }]).then(() => {}).catch(() => {})

      if (failedStatus.isLocked) {
        // Account is now locked
        setLockoutStatus(failedStatus)
        setCountdown(failedStatus.remainingSeconds)
        setError(getLockoutMessage(failedStatus))
      } else {
        // Not locked yet, show error with remaining attempts
        const remaining = failedStatus.remainingAttempts
        setError(
          `Failed to sign in: ${error.message}${
            remaining > 0
              ? ` (${remaining} attempt${remaining !== 1 ? 's' : ''} remaining before temporary lockout)`
              : ''
          }`
        )
      }
    }
    // Note: Don't set loading to false on success - let the redirect happen
  }

  const handleForgotPassword = async (e) => {
    e.preventDefault()

    if (!resetEmail) {
      setResetMessage('Please enter your email address')
      return
    }

    try {
      setResetMessage('')
      setResetLoading(true)

      const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {
        redirectTo: window.location.origin
      })

      if (error) throw error

      setResetMessage('✅ Password reset email sent! Check your inbox.')
      setTimeout(() => {
        setShowForgotPassword(false)
        setResetEmail('')
        setResetMessage('')
      }, 3000)
    } catch (error) {
      // Handle different error types
      if (error.message?.includes('429') || error.message?.includes('rate') || error.message?.includes('Too many')) {
        setResetMessage('⏱️ Please wait 60 seconds before requesting another password reset.')
      } else if (error.message?.includes('500') || error.message?.includes('Internal Server')) {
        setResetMessage('❌ Email service error. Please contact your administrator or try again later.')
      } else {
        setResetMessage('❌ Error: ' + error.message)
      }
    } finally {
      setResetLoading(false)
    }
  }

  const handlePasswordReset = async (e) => {
    e.preventDefault()

    if (newPassword !== confirmPassword) {
      setPasswordResetMessage('❌ Passwords do not match')
      return
    }

    if (newPassword.length < 12) {
      setPasswordResetMessage('❌ Password must be at least 12 characters')
      return
    }
    if (!/[A-Z]/.test(newPassword) || !/[0-9!@#$%^&*]/.test(newPassword)) {
      setPasswordResetMessage('❌ Password must contain at least one uppercase letter and one number or special character')
      return
    }

    try {
      setPasswordResetMessage('')
      setLoading(true)

      const { error } = await supabase.auth.updateUser({
        password: newPassword
      })

      if (error) throw error

      setPasswordResetMessage('✅ Password updated successfully! Redirecting...')

      // Clear the hash from URL and redirect to login
      setTimeout(() => {
        window.location.hash = ''
        window.location.reload()
      }, 2000)
    } catch (error) {
      setPasswordResetMessage('❌ Error: ' + error.message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'linear-gradient(135deg, #080f1e 0%, #0f1f3d 45%, #1e3a5f 100%)' }}>
      <div className="bg-white p-12 rounded-3xl shadow-2xl w-full max-w-md">
        {showPasswordReset ? (
          // PASSWORD RESET FORM
          <>
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Set New Password
              </h2>
              <p className="text-sm text-gray-600">
                Enter your new password below
              </p>
            </div>

            {passwordResetMessage && (
              <div className={`p-4 rounded-xl mb-6 border text-sm text-center ${
                passwordResetMessage.includes('✅')
                  ? 'bg-green-50 text-green-800 border-green-200'
                  : 'bg-red-50 text-red-800 border-red-200'
              }`}>
                {passwordResetMessage}
              </div>
            )}

            <form onSubmit={handlePasswordReset}>
              <div style={{ marginBottom: spacing.lg }}>
                <Input
                  type="password"
                  label="New Password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  required
                />
              </div>

              <div style={{ marginBottom: spacing.xl }}>
                <Input
                  type="password"
                  label="Confirm Password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  required
                />
              </div>

              <Button
                type="submit"
                variant="primary"
                size="lg"
                fullWidth
                loading={loading}
                disabled={loading}
              >
                Update Password
              </Button>
            </form>

            <div className="mt-8 pt-6 border-t border-gray-200 text-center">
              <p className="text-xs text-gray-400">
                Wake County ABC Law Enforcement
              </p>
            </div>
          </>
        ) : !showForgotPassword ? (
          // LOGIN FORM
          <>
            {/* Logo/Header Section */}
            <div className="text-center mb-10">
              <div className="mx-auto mb-4 flex items-center justify-center">
                <img
                  src="/logo.png"
                  alt="ABCAxis"
                  style={{ height: '120px', width: 'auto', objectFit: 'contain' }}
                />
              </div>
              <p className="text-sm font-semibold tracking-widest uppercase" style={{ color: '#3d6494', letterSpacing: '0.12em' }}>
                Intelligent Enforcement for ABC Agencies
              </p>
            </div>

            {error && (
              <div className="bg-red-50 text-red-800 p-4 rounded-xl mb-6 border border-red-200 text-sm flex items-center gap-2">
                <span className="text-xl">⚠️</span>
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: spacing.lg }}>
                <Input
                  type="email"
                  label="Email Address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="agent@wakeabc.com"
                  required
                  disabled={lockoutStatus && lockoutStatus.isLocked}
                />
              </div>

              <div style={{ marginBottom: spacing.xl }}>
                <Input
                  type="password"
                  label="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                  disabled={lockoutStatus && lockoutStatus.isLocked}
                />
              </div>

              {lockoutStatus && lockoutStatus.isLocked && (
                <div className="mb-4 bg-amber-50 text-amber-800 p-4 rounded-xl border border-amber-200 text-sm">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xl">🔒</span>
                    <span className="font-semibold">Account Temporarily Locked</span>
                  </div>
                  <p className="text-xs mb-2">
                    Too many failed login attempts. Please wait before trying again.
                  </p>
                  <div className="text-center mt-3">
                    <span className="text-2xl font-bold text-amber-900">
                      {formatLockoutTime(countdown)}
                    </span>
                  </div>
                </div>
              )}

              <Button
                type="submit"
                variant="primary"
                size="lg"
                fullWidth
                loading={loading}
                disabled={loading || (lockoutStatus && lockoutStatus.isLocked)}
              >
                {lockoutStatus && lockoutStatus.isLocked ? 'Locked' : 'Sign In'}
              </Button>
            </form>

            {/* Forgot Password Link */}
            <div className="mt-6 text-center">
              <button
                onClick={() => setShowForgotPassword(true)}
                className="text-blue-600 text-sm underline font-medium hover:text-blue-700"
              >
                Forgot Password?
              </button>
            </div>

            {/* Footer */}
            <div className="mt-8 pt-6 border-t border-gray-200 text-center">
              <p className="text-xs text-gray-400">
                Wake County ABC Law Enforcement
              </p>
              <p className="text-xs mt-2" style={{ color: '#9ca3af', lineHeight: '1.5' }}>
                This system is for authorized users only. Use constitutes consent to monitoring. Unauthorized access is prohibited and may be subject to criminal prosecution.
              </p>
            </div>
          </>
        ) : (
          // FORGOT PASSWORD FORM
          <>
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Reset Password
              </h2>
              <p className="text-sm text-gray-600">
                Enter your email to receive a password reset link
              </p>
            </div>

            {resetMessage && (
              <div className={`p-4 rounded-xl mb-6 border text-sm text-center ${
                resetMessage.includes('✅')
                  ? 'bg-green-50 text-green-800 border-green-200'
                  : 'bg-red-50 text-red-800 border-red-200'
              }`}>
                {resetMessage}
              </div>
            )}

            <form onSubmit={handleForgotPassword}>
              <div style={{ marginBottom: spacing.xl }}>
                <Input
                  type="email"
                  label="Email Address"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  placeholder="agent@wakeabc.com"
                  required
                />
              </div>

              <div style={{ display: 'flex', gap: spacing.sm }}>
                <Button
                  type="button"
                  variant="secondary"
                  size="lg"
                  fullWidth
                  onClick={() => {
                    setShowForgotPassword(false)
                    setResetEmail('')
                    setResetMessage('')
                  }}
                  disabled={resetLoading}
                >
                  Back
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  fullWidth
                  loading={resetLoading}
                  disabled={resetLoading}
                >
                  Send Reset Link
                </Button>
              </div>
            </form>

            {/* Footer */}
            <div className="mt-8 pt-6 border-t border-gray-200 text-center">
              <p className="text-xs text-gray-400">
                Wake County ABC Law Enforcement
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
